import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ClipboardCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import EmptyState from '../../components/Common/EmptyState';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { identityService, licenseService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { userFacingError } from '../../i18n/userFacingError';

function unwrapPlans(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

const PlanosRegularizacao = () => {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState(null);
  const [empreendimentoId, setEmpreendimentoId] = useState('');

  const { data, isLoading, error } = useQuery(['planos-regularizacao'], () =>
    licenseService.listPlanosRegularizacao().then((r) => r.data)
  );
  const { data: enterprises = [] } = useQuery(
    ['plano-enterprises'],
    () => fetchAllPages((params) => identityService.getEnterprises(params)),
    { staleTime: 5 * 60 * 1000 }
  );
  const enterpriseName = useMemo(() => {
    const map = {};
    for (const row of enterprises) map[row.id] = row.name || row.nome || row.id;
    return map;
  }, [enterprises]);
  const plans = unwrapPlans(data);
  const selected = plans.find((p) => p.id === openId) || plans[0];

  const generate = useMutation(
    () =>
      licenseService.generatePlanoRegularizacao(
        empreendimentoId ? { empreendimento_id: empreendimentoId } : {}
      ),
    {
    onSuccess: (res) => {
      toast.success(`Plano gerado. Score ${res.data?.score}%.`);
      if (res.data?.avisos?.length) toast.error(res.data.avisos.join(' '));
      setOpenId(res.data?.id || null);
      queryClient.invalidateQueries(['planos-regularizacao']);
      queryClient.invalidateQueries(['dash-plano']);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });

  const conclude = useMutation(
    ({ planId, itemId }) => licenseService.patchPlanoItem(planId, itemId, 'concluido'),
    {
      onSuccess: () => queryClient.invalidateQueries(['planos-regularizacao']),
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  return (
    <FormPageShell>
      <PageHeader
        title="Plano de regularização"
        description="Ações geradas a partir do score de compliance: condicionantes, licenças vencidas e denúncias abertas."
        breadcrumbs={[
          { label: 'Início', to: getAppHomePath() },
          { label: 'Licenças', to: '/licenses' },
          { label: 'Regularização' },
        ]}
      >
        <Can any={[P.LICENSE_LICENCAS, P.LICENSES_PAGE, P.REPORTS_VIEW]}>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="form-select"
              value={empreendimentoId}
              onChange={(e) => setEmpreendimentoId(e.target.value)}
            >
              <option value="">Todos os empreendimentos</option>
              {enterprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name || e.nome || e.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={generate.isLoading}
              onClick={() => generate.mutate()}
            >
              {generate.isLoading ? 'Gerando…' : 'Gerar plano'}
            </button>
          </div>
        </Can>
      </PageHeader>

      {isLoading ? (
        <p className="mt-6 text-gray-500">Carregando...</p>
      ) : error ? (
        <p className="mt-6 text-red-600">Não foi possível carregar os planos.</p>
      ) : plans.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={ClipboardCheck}
            title="Nenhum plano gerado"
            description="Gere o primeiro plano a partir do score de compliance."
            action={
              <button type="button" className="btn btn-primary" onClick={() => generate.mutate()}>
                Gerar plano
              </button>
            }
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="data-list-card overflow-hidden lg:col-span-1">
            <div className="border-b px-4 py-3 text-sm font-semibold">Histórico</div>
            <ul className="divide-y">
              {plans.map((plan) => (
                <li key={plan.id}>
                  <button
                    type="button"
                    className={`w-full px-4 py-3 text-left text-sm ${
                      selected?.id === plan.id ? 'bg-emerald-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setOpenId(plan.id)}
                  >
                    <div className="font-semibold">{plan.score}%</div>
                    <div className="text-xs text-gray-500">
                      {(plan.items || []).length} ação(ões) · {plan.status}
                      {plan.empreendimento_id
                        ? ` · ${enterpriseName[plan.empreendimento_id] || 'empreendimento'}`
                        : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="data-list-card overflow-hidden lg:col-span-2">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div>
                <p className="text-sm font-semibold">Score {selected?.score}%</p>
                <p className="text-xs text-gray-500">
                  {selected?.generated_at
                    ? String(selected.generated_at).replace('T', ' ').slice(0, 16)
                    : '—'}
                </p>
              </div>
              <Link to="/reports" className="text-sm text-primary-700">
                Relatório de compliance
              </Link>
            </div>
            {(selected?.items || []).length === 0 ? (
              <p className="px-4 py-8 text-sm text-gray-500">Nenhuma ação neste plano.</p>
            ) : (
              <ul className="divide-y">
                {(selected.items || []).map((item) => (
                  <li key={item.id} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                    <span>
                      <strong>{item.tipo}</strong> · {item.titulo}
                      <span className="ml-2 text-xs text-gray-500">({item.status})</span>
                    </span>
                    {item.status !== 'concluido' ? (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm shrink-0"
                        disabled={conclude.isLoading}
                        onClick={() => conclude.mutate({ planId: selected.id, itemId: item.id })}
                      >
                        Concluir
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </FormPageShell>
  );
};

export default PlanosRegularizacao;
